import { type BinarySource, type MedplumClient, OperationOutcomeError } from "@medplum/core";
import type { Binary, Reference } from "@medplum/fhirtypes";
import { FhirResource } from "@vibrant/models/fhir/FhirResource";
import { isMedplumResourceMissingOutcome } from "@/lib/errors/is-medplum-resource-missing-error";
import { ensureBinaryHasPracticeCompartment } from "@/lib/fhir/compartment";
import { createLogger, type Logger } from "@/lib/logger";
import { timeAmount } from "@/lib/time";
import { withRetry } from "@/lib/utils/retry";

const logger = createLogger("models/files/MedplumFile");

/** The Binary resource is absent; this does not classify failures from byte storage. */
export class MissingBinaryError extends OperationOutcomeError {}

/** Thrown when createBinary succeeds but returns no presigned URL (S3 upload failed silently). */
class MissingPresignedUrlError extends Error {
  constructor(public readonly binary: Binary) {
    super(`createBinary returned no presigned URL (binaryId: ${binary.id})`);
    this.name = "MissingPresignedUrlError";
  }
}

interface MedplumFileData {
  data: BinarySource;
  contentType: string;
  filename: string;
  practiceRef?: string | Reference;
  securityContext?: Reference;
  telemetry?: MedplumFileUploadTelemetry;
}

export interface MedplumFileUploadTelemetry {
  context?: Record<string, unknown>;
  logger?: Pick<Logger, "warn">;
}

export interface MedplumFileBlob {
  blob: Blob;
  contentType: string | undefined;
}

interface DeleteBinaryAfterUploadFailureInput {
  binaryId: string;
  logger: Pick<Logger, "warn">;
  medplum: MedplumClient;
  message: string;
  telemetry?: MedplumFileUploadTelemetry;
}

/**
 * Removes a Binary after an upload-side invariant fails, logging cleanup failures without masking
 * the original error.
 */
async function deleteBinaryAfterUploadFailure({
  binaryId,
  logger,
  medplum,
  message,
  telemetry,
}: DeleteBinaryAfterUploadFailureInput): Promise<void> {
  try {
    await MedplumFile.delete(medplum, binaryId);
  } catch (cleanupError) {
    logger.warn(message, {
      ...telemetry?.context,
      binaryId,
      error_type: cleanupError instanceof Error ? cleanupError.name : "UnknownError",
      outcome: "cleanup_failed",
      stage: "write.cleanup",
    });
  }
}

export class MedplumFile extends FhirResource<Binary> {
  static readonly ResourceType = "Binary" as const;

  //------------------------------------------------------------------------------------------------
  //                                 VIRTUAL PROPERTIES
  //------------------------------------------------------------------------------------------------

  get content(): string | undefined {
    return this.resource.data;
  }

  get contentType(): string | undefined {
    return this.resource.contentType;
  }

  get url(): string | undefined {
    return this.resource.url;
  }

  //------------------------------------------------------------------------------------------------
  //                                 STATIC METHODS
  //------------------------------------------------------------------------------------------------

  /**
   * Create a new file resource.
   *
   * @param medplum - The Medplum client.
   * @param request - The file request.
   * @returns The created file.
   */
  static async upload(medplum: MedplumClient, request: MedplumFileData): Promise<MedplumFile> {
    // Retry createBinary when the response has no presigned URL — this indicates
    // the Binary record was created but the S3 upload silently failed. Each retry
    // creates a new Binary record; orphaned empty Binaries from failed attempts
    // are harmless and cleaned up by Medplum's garbage collection.
    let binary: Binary;
    try {
      binary = await withRetry(
        async () => {
          const result = await medplum.createBinary({
            data: request.data,
            contentType: request.contentType,
            filename: request.filename,
            securityContext: request.securityContext,
          });
          if (!result.url) throw new MissingPresignedUrlError(result);
          return result;
        },
        {
          shouldRetry: (error) => error instanceof MissingPresignedUrlError,
          delay: timeAmount(1, "second"),
          maxRetries: 2, // 2 retries = 3 total attempts
        },
      );
    } catch (error) {
      if (error instanceof MissingPresignedUrlError) {
        // All retry attempts exhausted — use the last Binary, but warn loudly
        const telemetryLogger = request.telemetry?.logger ?? logger;
        telemetryLogger.warn("MedplumFile.upload missing presigned URL after retries", {
          ...request.telemetry?.context,
          binaryId: error.binary.id,
          outcome: "missing_presigned_url",
          stage: "write.binary",
        });
        if (error.binary.id) {
          await deleteBinaryAfterUploadFailure({
            medplum,
            binaryId: error.binary.id,
            telemetry: request.telemetry,
            logger: telemetryLogger,
            message: "Failed to delete binary after missing presigned URL",
          });
        }
        throw error;
      }

      throw error;
    }

    // Optionally set practice compartment if provided
    if (binary.id && request.practiceRef) {
      try {
        await ensureBinaryHasPracticeCompartment({
          medplum,
          binaryId: binary.id,
          practiceRef: request.practiceRef,
        });
      } catch (error) {
        const telemetryLogger = request.telemetry?.logger ?? logger;
        telemetryLogger.warn("MedplumFile.upload failed to assign practice compartment", {
          ...request.telemetry?.context,
          binaryId: binary.id,
          error_type: error instanceof Error ? error.name : "UnknownError",
          outcome: "practice_compartment_failed",
          stage: "write.binary",
        });

        await deleteBinaryAfterUploadFailure({
          medplum,
          binaryId: binary.id,
          telemetry: request.telemetry,
          logger: telemetryLogger,
          message: "Failed to delete binary after practice compartment failure",
        });

        throw error;
      }
    }

    return new MedplumFile(binary);
  }

  /**
   * Downloads the binary content.
   *
   * @param medplum - The Medplum client.
   * @param id - The id of the binary file.
   * @returns The file blob with content type.
   */
  static async download(medplum: MedplumClient, id: string): Promise<MedplumFileBlob> {
    let binary: Binary;
    try {
      // Binary has no FHIR search; read directly and preserve the original absence outcome.
      binary = await medplum.readResource(this.ResourceType, id);
    } catch (error) {
      if (isMedplumResourceMissingOutcome(error)) throw new MissingBinaryError(error.outcome);
      throw error;
    }
    // Storage failures must propagate, even when storage returns 404 or 410.
    const blob = await medplum.download(`Binary/${id}`);
    return { blob, contentType: binary.contentType };
  }
}
